import * as React from 'react';
import MediaImage, { ImageProps } from './MediaImage';
import MediaVideo from './MediaVideo';
import { MediaType } from './types';
import { useMediaType } from './useMediaType';
import { useAppShell } from '@ids/appshell';
import { IDSContentfulImageFile, IDSContentfulMedia, IDSContentfulVideoFile } from '@ids/types/contentful';

interface MediaPlaceholderProps {
    activeType: Exclude<MediaType, 'unsupported'>;
    'data-testid'?: string;
}

const MediaPlaceholder = ({
    children,
    activeType,
    'data-testid': testId,
}: React.PropsWithChildren<MediaPlaceholderProps>) => {
    const { logger } = useAppShell();

    let selectedType;

    React.Children.forEach(children, element => {
        if (
            React.isValidElement(element) &&
            (activeType === element.props.type || element.props.type.includes(activeType))
        ) {
            selectedType = React.cloneElement(element, { 'data-testid': testId });
        }
    });

    if (!React.isValidElement(selectedType)) {
        logger.error(`Media "${activeType}" is running without a matching type component.`);
    }

    return selectedType || null;
};

interface MediaTypeComponentProps {
    type: MediaType;
    className?: string;
}

const MediaTypeComponent = ({ children, type, ...restProps }: React.PropsWithChildren<MediaTypeComponentProps>) => (
    <div data-type={type} {...restProps}>
        {children}
    </div>
);

type MediaProps = {
    media: IDSContentfulMedia;
    className?: string;
    video?: React.VideoHTMLAttributes<HTMLVideoElement> & { ratio?: number };
    image?: ImageProps;

    /**
     * Accepts a boolean value.
     * If true it will preload the media component.
     *
     * @default false
     */
    priority?: boolean;
    'data-testid'?: string;
};

const Media = ({ priority = false, className, media, video, image, 'data-testid': testId }: MediaProps) => {
    const mediaType = useMediaType(media);

    if (mediaType === 'unsupported') {
        return null;
    }

    return (
        <MediaPlaceholder activeType={mediaType} data-testid={testId}>
            <MediaTypeComponent type="image" className={className}>
                <MediaImage priority={priority} image={media.image as IDSContentfulImageFile} {...image} />
            </MediaTypeComponent>
            <MediaTypeComponent type="video" className={className}>
                <MediaVideo priority={priority} video={media.video as IDSContentfulVideoFile} {...video} />
            </MediaTypeComponent>
        </MediaPlaceholder>
    );
};

Media.Image = MediaImage;
const Image = MediaImage;

Media.Video = MediaVideo;
const Video = MediaVideo;

export { Media as default, Image, MediaImage, Video, MediaVideo };
